import { Accordion, Badge, Button, Code, Group, Stack, Table, Text, Title } from '@mantine/core';
import { IconActivity, IconRefresh } from '@tabler/icons-react';
import { useState } from 'react';
import { fetchAdminHealth, useAdminHealth } from 'src/secret-menu/hooks/useAdminHealth';
import PageLoader from 'src/UI/loader/PageLoader';

const STATUS_COLORS: Record<string, string> = {
  healthy: 'green',
  degraded: 'yellow',
  unhealthy: 'red',
  unknown: 'gray',
};

export default function HealthCheckTab() {
  const { health, isLoading, isError, refetch, isRefetching } = useAdminHealth();
  const [isProbing, setIsProbing] = useState(false);

  const runProbe = async () => {
    setIsProbing(true);
    await fetchAdminHealth(true);
    await refetch();
    setIsProbing(false);
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError || !health) {
    return <Text c="red">Failed to load health check data</Text>;
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group gap="md">
          <Title order={3}>System Health</Title>
          <Badge size="lg" color={STATUS_COLORS[health.status]}>
            {health.status.toUpperCase()}
          </Badge>
        </Group>
        <Group gap="sm">
          <Button
            loading={isProbing}
            leftSection={<IconActivity size={16} />}
            variant="light"
            onClick={runProbe}
          >
            Run Probe
          </Button>
          <Button
            loading={isRefetching}
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      <Stack gap="md">
        <Title order={4}>Database</Title>
        <Table maw={600}>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td w={120}>Status</Table.Td>
              <Table.Td>
                <Badge color={STATUS_COLORS[health.db.status]}>{health.db.status}</Badge>
              </Table.Td>
            </Table.Tr>
            {health.db.message && (
              <Table.Tr>
                <Table.Td>Message</Table.Td>
                <Table.Td>
                  <Code block>{health.db.message}</Code>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Stack>

      <Stack gap="md">
        <Group gap="md">
          <Title order={4}>LLM Providers</Title>
          <Badge color={STATUS_COLORS[health.llm.status]}>{health.llm.status}</Badge>
          <Text size="sm" c="dimmed">
            ({health.llm.healthyCount}/{health.llm.totalCount} healthy)
          </Text>
        </Group>

        <Accordion variant="contained">
          {health.llm.models.map((model, index) => (
            <Accordion.Item key={index} value={`model-${index}`} role="region">
              <Accordion.Control>
                <Group gap="md">
                  <Badge color={STATUS_COLORS[model.status]}>{model.status}</Badge>
                  <Code>{model.provider}</Code>
                  <Text size="sm" c="dimmed" style={{ userSelect: 'text' }}>
                    {model.model}
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                {model.message ? (
                  <Code block style={{ whiteSpace: 'pre-wrap' }}>
                    {model.message}
                  </Code>
                ) : (
                  <Text c="dimmed" size="sm">
                    No error message
                  </Text>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Stack>
    </Stack>
  );
}
